import _ from 'lodash';
import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { Field } from 'react-final-form';
import { FieldArrayRenderProps } from 'react-final-form-arrays';
import {
  FormDropdown,
  FormMultiSelectCheckbox,
} from '../../../../../components/redux-form-inputs/redux-form-inputs';
import { getProjects } from '../../../../../redux/projects/projects.selectors';
import { StoreState } from '../../../../../redux/reducers/store.types';
import { DropdownOption, Project } from '../../../../../types/entities';
import {
  TeamMemberFormAccessLevel,
  TeamMemberFormAccessPermissions,
} from '../../../team.constants';
import { TeamMemberAccess } from '../../../team.types';

import styles from './team-member-form-fields.scss';

interface OwnProps {
  projects: Project[];
}

type Props = OwnProps &
  WrappedComponentProps &
  FieldArrayRenderProps<TeamMemberAccess, any>;

const TeamMemberFormFieldsComponent: React.FunctionComponent<Props> = ({
  projects,
  fields,
  intl,
}: Props) => {
  function getAccessLevelOptions() {
    return [
      {
        id: TeamMemberFormAccessLevel.Organization,
        value: intl.formatMessage({
          id: 'team.add-member-modal.form.access-level.options.organization',
        }),
      },
      {
        id: TeamMemberFormAccessLevel.Project,
        value: intl.formatMessage({
          id: 'team.add-member-modal.form.access-level.options.project',
        }),
      },
    ];
  }

  function getAccessPermissionsOptions() {
    return [
      {
        id: TeamMemberFormAccessPermissions.Viewer,
        value: intl.formatMessage({
          id: 'team.add-member-modal.form.access-permissions.options.view',
        }),
      },
      {
        id: TeamMemberFormAccessPermissions.Admin,
        value: intl.formatMessage({
          id: 'team.add-member-modal.form.access-permissions.options.edit',
        }),
      },
      {
        id: TeamMemberFormAccessPermissions.None,
        value: intl.formatMessage({
          id: 'team.add-member-modal.form.access-permissions.options.none',
        }),
      },
    ];
  }

  function getProjectsOptions(): DropdownOption[] {
    return _.map(projects, (project: Project) => ({
      id: project.id,
      value: project.name,
    }));
  }

  function valueParser({ id }: DropdownOption): string {
    switch (id) {
      case TeamMemberFormAccessPermissions.Viewer:
        return intl.formatMessage({
          id: 'team.add-member-modal.form.access-permissions.selected.view',
        });
      case TeamMemberFormAccessPermissions.Admin:
        return intl.formatMessage({
          id: 'team.add-member-modal.form.access-permissions.selected.edit',
        });
      default:
        return intl.formatMessage({
          id: 'team.add-member-modal.form.access-permissions.selected.none',
        });
    }
  }

  return (
    <ul className={styles.fields}>
      {fields.map((member, index) => (
        <li
          className={styles.fieldsGroup}
          data-hook="team-member-form-fields-item"
          key={index}
        >
          <Field
            id="accessLevel"
            name={`${member}.level`}
            component={FormDropdown}
            placeholder={intl.formatMessage({
              id: 'team.add-member-modal.form.access-level.placeholder',
            })}
            props={
              {
                dataHook: `team-member-form-access-level-dropdown-${index}`,
                noRightBorderRadius: true,
              } as any
            }
            options={getAccessLevelOptions()}
          />

          {fields.value[index].level === TeamMemberFormAccessLevel.Project ? (
            <div className={styles.accessProjectsDropdown}>
              <Field
                name={`${member}.projectsIds`}
                component={FormMultiSelectCheckbox}
                props={
                  {
                    dataHook: `team-member-form-access-projects-multiselect-${index}`,
                    noLeftBorderRadius: true,
                    noRightBorderRadius: true,
                  } as any
                }
                options={getProjectsOptions()}
              />
            </div>
          ) : null}

          <div className={styles.accessPermissionsDropdown}>
            <Field
              id="accessPermissions"
              name={`${member}.permissions`}
              dropdownWidth="200px"
              component={FormDropdown}
              props={
                {
                  dataHook: `team-member-form-access-permissions-dropdown-${index}`,
                  noLeftBorderRadius: true,
                } as any
              }
              valueParser={valueParser}
              options={getAccessPermissionsOptions()}
            />
          </div>
        </li>
      ))}
    </ul>
  );
};

export const TeamMemberFormFields = compose<
  Props,
  FieldArrayRenderProps<TeamMemberAccess, any>
>(
  injectIntl,
  connect((state: StoreState) => ({
    projects: getProjects(state),
  })),
)(TeamMemberFormFieldsComponent);
